import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { MessagesSquare, Utensils } from 'lucide-react';

import { DocumentMathTextBlock } from '../../components/MathTextBlock';
import { CONTENT_FONT_SIZE_PX } from '../../theme/contentTypography';
import type { Ttq1qnqSegmentItem } from './TtqBottomPanel1qnq';

type TtqSegmentSelectionPopupProps = {
  open: boolean;
  items: Ttq1qnqSegmentItem[];
  /** 锚定最后点选的段 */
  anchorObjectId: string | null;
  /** 画布 viewport 变化时重算位置（平移跟随） */
  viewportKey?: string;
  /** 与画布同缩放，使选段预览字号与高亮原文视觉一致 */
  canvasZoom?: number;
  /** 鸟瞰收敛卡等场景：只提供 AI 对话 */
  chatOnly?: boolean;
  onOpenChat: () => void;
  onOpenPinTi: () => void;
  /** KP：反馈报错（仍开 AI 对话，纠错意图） */
  onReportError?: () => void;
  onClose?: () => void;
  /** 清空已选段 */
  onClear?: () => void;
};

type PopupPos = {
  top: number;
  left: number;
};

function measureAnchor(objectId: string): PopupPos | null {
  const el = document.querySelector(`[data-object-id="${CSS.escape(objectId)}"]`);
  if (!(el instanceof HTMLElement)) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width < 1 && rect.height < 1) return null;
  if (rect.bottom < 0 || rect.top > window.innerHeight) return null;
  const popupWidth = 280;
  const left = Math.max(12, Math.min(rect.left, window.innerWidth - popupWidth - 12));
  const top = Math.min(window.innerHeight - 160, Math.max(12, rect.bottom + 8));
  return { top, left };
}

/** 选段近旁：已选列表 + AI 对话 / 品题；与高亮同生共死 */
export function TtqSegmentSelectionPopup({
  open,
  items,
  anchorObjectId,
  viewportKey = '',
  canvasZoom = 1,
  chatOnly = false,
  onOpenChat,
  onOpenPinTi,
  onReportError,
  onClose,
  onClear,
}: TtqSegmentSelectionPopupProps) {
  const [pos, setPos] = useState<PopupPos | null>(null);
  const showErrorFeedback = Boolean(onReportError);

  /** 画布上的题文会随 viewport.zoom 缩放；Popup 在屏幕坐标系，需同比例缩小字号 */
  const previewFontPx = useMemo(() => {
    const zoom = Number.isFinite(canvasZoom) && canvasZoom > 0 ? canvasZoom : 1;
    return Math.max(11, Math.min(CONTENT_FONT_SIZE_PX, Math.round(CONTENT_FONT_SIZE_PX * zoom * 10) / 10));
  }, [canvasZoom]);

  useLayoutEffect(() => {
    if (!open || !anchorObjectId || items.length === 0) {
      setPos(null);
      return;
    }
    setPos(measureAnchor(anchorObjectId));
  }, [open, anchorObjectId, items.length, viewportKey]);

  useEffect(() => {
    if (!open || !anchorObjectId) return;
    const onScrollOrResize = () => {
      setPos(measureAnchor(anchorObjectId));
    };
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, anchorObjectId]);

  if (!open || items.length === 0 || !pos) return null;

  return (
    <div
      className="fixed z-50 w-[280px] overflow-hidden rounded-xl border border-border bg-surface shadow-lg"
      style={{ top: pos.top, left: pos.left }}
      role="dialog"
      aria-label="选段操作"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5">
        <span className="text-[11px] font-medium text-muted">已选 {items.length} 段</span>
        {onClear ? (
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] font-medium text-primary hover:underline"
          >
            清空
          </button>
        ) : null}
      </div>
      <div className="max-h-28 space-y-1 overflow-y-auto border-b border-border px-3 py-2">
        {items.map((item, index) => (
          <div key={item.objectId} className="line-clamp-2 text-ink" style={{ fontSize: previewFontPx, lineHeight: 1.45 }}>
            <span className="mr-1 font-semibold text-primary">{index + 1}.</span>
            <DocumentMathTextBlock
              text={item.text}
              inline
              style={{ fontSize: previewFontPx, lineHeight: 1.45 }}
              className="[&_.katex]:!text-[1em]"
            />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onOpenChat}
          className={
            chatOnly && !showErrorFeedback
              ? 'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-2 py-2 text-xs font-medium text-white hover:bg-primary/90'
              : 'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-2 py-2 text-xs font-medium text-ink hover:bg-black/[0.04]'
          }
        >
          <MessagesSquare size={15} />
          AI 对话
        </button>
        {showErrorFeedback ? (
          <button
            type="button"
            onClick={onReportError}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-2 py-2 text-xs font-medium text-white hover:bg-primary/90"
          >
            反馈报错
          </button>
        ) : null}
        {chatOnly ? null : (
          <button
            type="button"
            onClick={onOpenPinTi}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-2 py-2 text-xs font-medium text-white hover:bg-primary/90"
          >
            <Utensils size={15} />
            品题
          </button>
        )}
      </div>
    </div>
  );
}
